import { ChangeEvent } from 'react'
import { isEqual } from 'lodash'
import { KeyboardArrowDown } from '@mui/icons-material'

import {
  TextField,
  Typography,
  Checkbox,
  Tooltip,
  InputLabelProps,
  AutocompleteProps,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  Chip,
} from '@mui/material'
import { AutocompleteCustomStyle } from '@/theme/autocomplete.theme'

interface Props
  extends Omit<
    AutocompleteProps<any, boolean, true, true>,
    'renderInput' | 'onChange' | 'value'
  > {
  label?: string
  onChange: (
    event: ChangeEvent<{}>,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any> | undefined,
  ) => void
  margin?: 'dense' | 'normal' | 'none'
  value?: any
  placeholder?: string
  inputLabelProps?: Partial<InputLabelProps>
  chipTags?: boolean
  error?: boolean
  helperText?: string
  name?: string
  inputOnChange?: (value: string) => void
}

const AutocompleteCustom = ({
  label,
  margin,
  multiple,
  limitTags,
  placeholder,
  inputLabelProps,
  getOptionLabel,
  value,
  chipTags,
  error,
  helperText,
  name,
  inputOnChange,
  ...rest
}: Props) => {
  const limit = limitTags || 1

  return (
    <AutocompleteCustomStyle
      isOptionEqualToValue={(option, value) => isEqual(value, option)}
      getOptionLabel={getOptionLabel}
      value={value}
      className={chipTags ? 'show-chips' : ''}
      multiple={multiple}
      renderInput={(params) => {
        const {
          inputProps: { onChange: ownOnChange },
        } = params as any

        return (
          <TextField
            {...params}
            InputLabelProps={inputLabelProps || undefined}
            margin={margin || 'normal'}
            fullWidth
            variant="outlined"
            label={label || ''}
            placeholder={placeholder || ''}
            error={error}
            helperText={helperText}
            name={name}
            inputProps={{
              ...params.inputProps,
              onChange: (event: any) => {
                inputOnChange && inputOnChange(event.currentTarget.value)
                return ownOnChange(event)
              },
            }}
          />
        )
      }}
      limitTags={limit}
      popupIcon={<KeyboardArrowDown />}
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props}>
            {multiple && <Checkbox checked={selected} sx={{ pl: 0 }} />}
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              variant="body2"
            >
              {getOptionLabel && getOptionLabel(option)}
            </Typography>
          </li>
        )
      }}
      disableCloseOnSelect={multiple}
      renderTags={(value, getTagProps) => {
        return (
          <>
            {chipTags ? (
              value
                .slice(0, limit)
                .map((option, index) => (
                  <Chip
                    size="small"
                    label={getOptionLabel && getOptionLabel(option)}
                    {...getTagProps({ index })}
                  />
                ))
            ) : (
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  mx: 1,
                }}
              >
                {value
                  .slice(0, limit)
                  .map((option) => getOptionLabel && getOptionLabel(option))
                  .join(', ')}
              </Typography>
            )}
            {value.length > limit && (
              <Tooltip
                arrow
                title={value
                  .map((option) => getOptionLabel && getOptionLabel(option))
                  .join(', ')}
              >
                <Typography
                  variant="caption"
                  component="p"
                  sx={{ fontWeight: 600 }}
                >
                  +{value.length - limit}
                </Typography>
              </Tooltip>
            )}
          </>
        )
      }}
      {...rest}
    />
  )
}

export default AutocompleteCustom
